<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" @close="emit('close')">
    <template #content="{ closeModal }">
      <!-- Pick summary form -->
      <div class="pick-summary-form">
        <div class="pick-summary-form__header">
          <h1 class="global-h1 pick-summary-form__title">Pick an Album to use</h1>
          <!-- / Modal title -->
        </div>

        <!-- Filters panel -->
        <div v-if="!isLoading" class="filters-panel pick-summary-form__filters-panel">
          <select class="global-select filters-panel__dropdown" v-model="selectedType">
            <option value="All">All Types</option>
            <option v-for="type in allContextTypes" :key="type" :value="type">{{ getContextTypeLabel(type) }}s</option>
          </select>
          <input class="global-text-input filters-panel__search" v-model="searchText" placeholder="Search by name..." type="text" />
        </div>
        <div v-if="!isLoading && props.videoOnly" class="filters-panel__only-videos pick-summary-form__only-videos-filter">
          <SrpCheckbox>
            <template #input><input type="checkbox" v-model="onlyVideosFilter" /></template>
            <template #text>Only videos</template>
          </SrpCheckbox>
        </div>
        <!-- / Filters panel -->

        <div v-if="isLoading">
          <LoaderNew style="width: 50px; height: 50px; margin: 0 auto" :borderWidth="3" />
        </div>
        <div v-else-if="showNoVideosEmptyState" class="pick-summary-form__empty-state">
          No albums currently have videos.
          <LinkWithIcon tag="RouterLink" :to="{ name: 'CommunityPhotos', params: { pageId: props.customerId } }" isDottedUnderline style="margin-left: 4px">
            <span>Content Library</span>
          </LinkWithIcon>
        </div>
        <div v-else-if="showNoMatchesEmptyState" style="margin-bottom: 30px; text-align: center">No matches found</div>

        <ul v-else-if="!isLoading && allSummariesGroupedAndFiltered?.length" class="pick-summary-form__sections-list">
          <!-- Summaries section -->
          <li class="summaries-section pick-summary-form__summaries-section" v-for="group in allSummariesGroupedAndFiltered" :key="group.contextType">
            <!-- Section title -->
            <div class="section-title summaries-section__section-title">
              <h5 class="global-h5 section-title__text">{{ group.summaries.length }} {{ getContextTypeLabel(group.contextType) }}{{ group.summaries.length > 1 ? "s" : "" }}</h5>
            </div>
            <!-- / Section title -->

            <ul class="summaries-section__summaries-list">
              <!-- Summary container -->
              <li class="summary-container summaries-section__summary-container" v-for="summary in group.summaries" :key="summary.id">
                <ContentSummary
                  class="summary-container__summary"
                  :contentSummaryData="summary"
                  :customerId="props.customerId"
                  :isContentSourcedFromLabelVisible="false"
                  :showVideoCount="props.videoOnly || props.showPhotoAndVideoCount"
                  @clickViewPhotos="
                    () => {
                      viewAllPhotosSummaryId = summary.id;
                      isViewAllPhotosModalVisible = true;
                    }
                  "
                />

                <SrpButton
                  class="summary-container__pick-button"
                  :isDisabled="props.videoOnly && (summary.videoCount ?? 0) === 0"
                  @click="
                    () => {
                      emit('picked', summary);
                      closeModal();
                    }
                  "
                >
                  Pick
                </SrpButton>
              </li>
              <!-- / Summary container -->
            </ul>
          </li>
          <!-- / Summaries section -->
        </ul>
      </div>
      <!-- / Pick summary form -->

      <ViewAllPhotosModal v-if="isViewAllPhotosModalVisible" v-model:isVisible="isViewAllPhotosModalVisible" :customerId="props.customerId" :contentSummaryId="viewAllPhotosSummaryId" :zIndex="82" />
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import ContentSummaryRepo from "@repos/ContentSummaryRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";

// Types
import { ContentCalendarItem, ContentSummaryContextType, ContentSummaryPreview } from "@contracts/contentCalendar";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";

// Translate contextType to readable text
function getContextTypeLabel(contextType: ContentSummaryContextType): string {
  switch (contextType) {
    case ContentSummaryContextType.CommunityUpload:
      return "Community Upload";
    case ContentSummaryContextType.CustomerUpload:
      return "My Upload";
    case ContentSummaryContextType.CreatorVisit:
      return "Creator Visit";
    case ContentSummaryContextType.Location:
      return "Location";
    default:
      return contextType;
  }
}

// Order of Sources for display
const contextTypeDisplayOrder: Array<ContentSummaryContextType> = [
  ContentSummaryContextType.CustomerUpload,
  ContentSummaryContextType.Location,
  ContentSummaryContextType.CommunityUpload,
  ContentSummaryContextType.CreatorVisit,
];

function compareContextTypes(a: ContentSummaryContextType, b: ContentSummaryContextType): number {
  const aOrder = contextTypeDisplayOrder.indexOf(a);
  const bOrder = contextTypeDisplayOrder.indexOf(b);

  if (aOrder !== -1 && bOrder !== -1) return aOrder - bOrder;
  if (aOrder !== -1) return -1;
  if (bOrder !== -1) return 1;

  return getContextTypeLabel(a).localeCompare(getContextTypeLabel(b));
}

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    calendarItem: ContentCalendarItem | null;
    customerId: string;
    videoOnly?: boolean;
    showPhotoAndVideoCount?: boolean;
  }>(),
  {
    videoOnly: false,
    showPhotoAndVideoCount: false,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "close"): void;
  (e: "picked", contentSummary: ContentSummaryPreview): void;
}>();

// Load all summaries =========================================================
const allSummaries = ref<Array<ContentSummaryPreview>>([]);
const isLoading = ref<boolean>(false);

onMounted(async () => {
  isLoading.value = true;

  const response = await ContentSummaryRepo.getAllSummariesPreview(props.customerId);
  allSummaries.value = response.content;

  isLoading.value = false;
});

interface SummariesGroup {
  contextType: ContentSummaryContextType;
  summaries: Array<ContentSummaryPreview>;
}

const allSummariesGroupedAndFiltered = computed<Array<SummariesGroup>>(() => {
  const allGroups: Array<SummariesGroup> = [];

  allSummaries.value.forEach(summary => {
    // Apply contextType filter
    if (selectedType.value !== "All" && summary.contextType !== selectedType.value) {
      return;
    }

    // Apply searchText filter (case-insensitive)
    if (searchText.value && summary.contextName) {
      const searchLower = searchText.value.toLowerCase();
      const contextNameLower = summary.contextName.toLowerCase();
      if (!contextNameLower.includes(searchLower)) {
        return;
      }
    }

    if (props.videoOnly && onlyVideosFilter.value && (summary.videoCount ?? 0) <= 0) {
      return;
    }

    // check if allGroups includes the current context type
    let groupIndex = allGroups.findIndex(g => g.contextType === summary.contextType);

    if (groupIndex === -1) {
      // create the group if it doesn't exist
      allGroups.push({ contextType: summary.contextType, summaries: [] });
      groupIndex = allGroups.length - 1;
    }

    // apply filter
    allGroups[groupIndex].summaries.push(summary);
  });

  // Sort summaries within each group by contentDate, fallback to lastProcessed
  allGroups.forEach(group => {
    group.summaries.sort((a, b) => {
      const dateA = a.contentDate || a.lastProcessed;
      const dateB = b.contentDate || b.lastProcessed;

      // Handle null values (put them at the end)
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Compare dates (descending order - most recent first)
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  });

  return allGroups.sort((a, b) => compareContextTypes(a.contextType, b.contextType));
});

// Defines filters ============================================================
const selectedType = ref<string>("All");
const searchText = ref<string>("");
const onlyVideosFilter = ref<boolean>(true);

watch(
  () => [props.isVisible, props.videoOnly] as const,
  ([isVisible, videoOnly]) => {
    if (isVisible && videoOnly) {
      onlyVideosFilter.value = true;
    }
  }
);

const hasAnyAlbumWithVideos = computed(() => allSummaries.value.some(summary => (summary.videoCount ?? 0) > 0));

const showNoVideosEmptyState = computed(
  () => props.videoOnly && onlyVideosFilter.value && !allSummariesGroupedAndFiltered.value.length && !hasAnyAlbumWithVideos.value
);

const showNoMatchesEmptyState = computed(() => !isLoading.value && !allSummariesGroupedAndFiltered.value.length && !showNoVideosEmptyState.value);

const allContextTypes = computed<Array<ContentSummaryContextType>>(() => {
  return [...new Set(allSummaries.value.map(s => s.contextType))].sort(compareContextTypes);
});

// Toggle the "View All Photos" modal =========================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Filters panel ==============================================================
.filters-panel {
  gap: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &__dropdown {
    min-width: 200px;
  }

  &__search {
    flex: 1;
    min-width: 200px;
  }

  &__only-videos {
    margin-top: -13px;
    margin-bottom: 25px;
  }
}

// Section title ==============================================================
.section-title {
  gap: 15px;
  display: flex;

  &::after {
    content: "";
    flex-grow: 1;
    height: 11px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }
}

// Summary container ==========================================================
.summary-container {
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 0;
  background: rgba(0, 0, 0, 0.05);

  &__summary {
    width: 100%;
    padding: 0 !important;

    &::before {
      display: none !important;
    }

    :deep(.content-summary-snippet__title) {
      inset: -11px auto auto 11px;
    }
  }

  &__pick-button {
    margin-right: 15px;
  }
}

// Summaries section ==========================================================
.summaries-section {
  &__section-title {
    margin-bottom: 25px;
  }

  &__summaries-list {
    gap: 12px;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
  }

  &__summary-container {
    width: 100%;
  }
}

// Pick summary form ==========================================================
.pick-summary-form {
  min-height: 500px;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__header {
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__title {
    padding-bottom: 15px;
  }

  &__filters-panel {
    margin-bottom: 25px;
  }

  &__only-videos-filter {
    margin-top: -13px;
    margin-bottom: 25px;
  }

  &__empty-state {
    margin-bottom: 30px;
    text-align: center;
  }

  &__sections-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__summaries-section {
    margin-bottom: 40px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
